import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, of, pairwise } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  groupBy,
  map,
  mapTo,
  mergeMap,
  skipUntil,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { isEmpty } from 'lodash';
import {
  acceptEmailConversation,
  closeEmailConversation,
  emailConversationChanged,
  emailConversationsLoaded,
  initializeModule,
  loadEmailConversations,
  transferEmailConversationToUser,
  transferEmailConversationToQueue,
  transferEmailConversationFailure,
  transferEmailConversationSuccess,
  loadWrapUpsForEmailConversation,
  wrapUpsLoadedForEmailConversation,
  saveWrapUpForEmailConversation,
  skipWrapUpForEmailConversation,
  evictEmailConversation,
  createEmailConversation,
  createEmailConversationFailure,
  createEmailConversationSuccess
} from '../actions/email-conversation.actions';
import { EmailConversationApiService } from '../services/email-conversation-api.service';
import { EmailConversationConverterService } from '../services/email-conversation-converter.service';
import {
  connectToNotificationChannelSuccess,
  notificationArrived
} from '../../../notifications/actions/notification.actions';
import { selectConversationsNotificationTopicName } from '../../common/selectors/conversation.selectors';
import { ConversationResponse } from '../../common/models/conversation-response.models';
import {
  closeConversationWorkItem,
  openConversationWorkItem
} from '../../common/actions/conversation-work-item.actions';
import {
  selectCommunicationId,
  selectEmailConversationById,
  selectWrapUpsByConversationId
} from '../selectors/email-conversation.selectors';
import { selectUser } from 'common/store/genesys-cloud/users/selectors/user.selectors';
import { WrapUpCode } from '../../common/models/wrap-up-code.model';
import { AutoSavePersistService } from 'common/auto-save/auto-save-persist.service';
import {
  getEmailDraftStorageKey,
  getEmailFooterModeStorageKey
} from 'common/store/genesys-cloud/conversations/emails/helpers/email-drafts.helpers';

@Injectable({
  providedIn: 'root'
})
export class GenesysCloudEmailConversationEffects {
  private readonly existingUser$ = this.store.select(selectUser).pipe(filter((user) => !!user));
  private readonly initializeModule$ = this.actions$.pipe(ofType(initializeModule));

  readonly init$ = createEffect(() =>
    combineLatest(this.initializeModule$, this.existingUser$).pipe(map(() => loadEmailConversations()))
  );

  readonly reloadConversationsOnReconnect$ = createEffect(() =>
    this.actions$.pipe(
      skipUntil(this.initializeModule$),
      ofType(connectToNotificationChannelSuccess),
      filter(({ isReconnect }) => isReconnect),
      map(() => loadEmailConversations())
    )
  );

  readonly loadConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEmailConversations),
      switchMap(() => this.api.getEmailConversations$()),
      concatLatestFrom(() => this.existingUser$),
      map(([conversations, agentUser]) =>
        conversations.map((conversation) => this.converter.convert(conversation, agentUser.id))
      ),
      map((extendedConversations) => emailConversationsLoaded({ conversations: extendedConversations }))
    )
  );

  readonly handleEmailConversationNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationArrived),
      concatLatestFrom(() => this.store.select(selectConversationsNotificationTopicName)),
      filter(([{ notification }, topicName]) => notification.topicName === topicName),
      map(([{ notification }, _]) => <ConversationResponse>notification.eventBody),
      filter(GenesysCloudEmailConversationEffects.isEmailType),
      concatLatestFrom(() => this.existingUser$),
      map(([eventBody, agentUser]) => this.converter.convert(eventBody, agentUser.id)),
      map((conversation) => emailConversationChanged({ conversation }))
    )
  );

  readonly conversationAccept$ = createEffect(() =>
    this.actions$.pipe(
      ofType(acceptEmailConversation),
      concatLatestFrom(({ conversationId }) => this.store.select(selectEmailConversationById(conversationId))),
      filter(([_, conversation]) => conversation.isAcceptable),
      mergeMap(([{ focus }, conversation]) =>
        this.api
          .acceptConversation$(conversation.id, conversation.agentParticipant.id)
          .pipe(mapTo(openConversationWorkItem({ conversationId: conversation.id, focus })))
      )
    )
  );

  readonly conversationClose$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(closeEmailConversation),
        concatLatestFrom(({ conversationId }) => this.store.select(selectEmailConversationById(conversationId))),
        mergeMap(([_, conversation]) => this.api.closeConversation$(conversation.id, conversation.agentParticipant.id))
      ),
    { dispatch: false }
  );

  readonly transferEmailConversationToUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transferEmailConversationToUser),
      mergeMap(({ conversationId, participantId, userId }) =>
        this.api.transferConversationToUser$(conversationId, participantId, userId).pipe(
          map(() => transferEmailConversationSuccess({ conversationId })),
          catchError(() => of(transferEmailConversationFailure({ conversationId })))
        )
      )
    )
  );

  readonly transferEmailConversationToQueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transferEmailConversationToQueue),
      mergeMap(({ conversationId, participantId, queueId }) =>
        this.api.transferConversationToQueue$(conversationId, participantId, queueId).pipe(
          map(() => transferEmailConversationSuccess({ conversationId })),
          catchError(() => of(transferEmailConversationFailure({ conversationId })))
        )
      )
    )
  );

  readonly createEmailConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createEmailConversation),
      mergeMap(({ queueId, toAddress }) =>
        this.api.createConversation$(queueId, toAddress).pipe(
          map(({ id }) => createEmailConversationSuccess({ conversationId: id })),
          catchError((errorResponse: any) =>
            of(createEmailConversationFailure({ message: errorResponse?.error?.message }))
          )
        )
      )
    )
  );

  readonly openCreatedEmailConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createEmailConversationSuccess),
      switchMap(({ conversationId }) =>
        this.store.select(selectEmailConversationById(conversationId)).pipe(
          // wait until conversation and customer participant is loaded, to avoid panel errors and missing email address
          filter((conversation) => !!conversation?.customerParticipant),
          take(1)
        )
      ),
      map((conversation) => openConversationWorkItem({ conversationId: conversation.id, focus: true }))
    )
  );

  readonly loadWrapUpsForEmailConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadWrapUpsForEmailConversation),
      concatLatestFrom(({ conversationId }) => this.store.select(selectWrapUpsByConversationId(conversationId))),
      filter(([_, wrapUpCodes]) => !this.areWrapUpCodesLoaded(wrapUpCodes)),
      concatLatestFrom(([{ conversationId }]) => this.store.select(selectEmailConversationById(conversationId))),
      mergeMap(([_, conversation]) =>
        this.api
          .getWrapUpCodes$(conversation.id, conversation.agentParticipant.id)
          .pipe(
            map((wrapUpCodes) => wrapUpsLoadedForEmailConversation({ conversationId: conversation.id, wrapUpCodes }))
          )
      )
    )
  );

  readonly skipWrapUpForEmailConversation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(skipWrapUpForEmailConversation),
        concatLatestFrom(({ conversationId }) => [this.store.select(selectEmailConversationById(conversationId))]),
        mergeMap(([_, conversation]) =>
          this.api.skipWrapUpForConversation$(conversation.id, conversation.agentParticipant.id)
        )
      ),
    { dispatch: false }
  );

  readonly saveWrapUpForEmailConversation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveWrapUpForEmailConversation),
        concatLatestFrom(({ conversationId }) => [
          this.store.select(selectEmailConversationById(conversationId)),
          this.store.select(selectCommunicationId(conversationId))
        ]),
        mergeMap(([action, conversation, communicationId]) =>
          this.api.wrapUpConversation$(
            conversation.id,
            conversation.agentParticipant.id,
            communicationId,
            action.wrapUpCode
          )
        )
      ),
    { dispatch: false }
  );

  readonly evictEmailConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(emailConversationChanged),
      filter(({ conversation }) => !conversation.isActive),
      map(({ conversation }) => evictEmailConversation({ conversationId: conversation.id })),
      delay(0)
    )
  );

  readonly closeEmailWorkItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(emailConversationChanged),
      groupBy((c) => c.conversation.id),
      mergeMap((group) =>
        group.pipe(
          pairwise(),
          filter(
            ([previous, next]) =>
              previous.conversation.isAcceptable === false && next.conversation.isAcceptable === true
          ),
          map(([previous, _]) => closeConversationWorkItem({ conversationId: previous.conversation.id })),
          tap((c) => {
            this.autoSavePersistService.clear(getEmailFooterModeStorageKey(c.conversationId));
            this.autoSavePersistService.clear(getEmailDraftStorageKey(c.conversationId));
          })
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly api: EmailConversationApiService,
    private readonly converter: EmailConversationConverterService,
    private readonly store: Store,
    private readonly autoSavePersistService: AutoSavePersistService
  ) {}

  private static isEmailType(conversationResponse: ConversationResponse): boolean {
    return conversationResponse?.participants.some((p) => !isEmpty(p.emails));
  }

  private areWrapUpCodesLoaded(wrapUpCodes: WrapUpCode[]): boolean {
    return !!wrapUpCodes && wrapUpCodes.length > 0;
  }
}
